import type { AppGridColumn, GridColumnState, GridSortItem } from './types';

/** A saved column layout to start from (widths are applied by the grid itself). */
export type SavedColumnState = Omit<GridColumnState, 'widths'>;

/**
 * What the screen DECLARES on its columns (groupIndex, sortOrder / sortIndex,
 * visible, fixed) versus what the user has done since (the grid's state).
 *
 * When the declarations change, only the setting that changed is adopted, for
 * the column it changed on: hiding a column keeps the user's sort, grouping,
 * pinning, order and widths; a toggled groupIndex regroups that column only.
 * A column that appears later (conditionally included in the array) takes its
 * DECLARED position among its neighbours, not the end.
 */

export interface DeclaredCol {
  gi?: number;
  so?: 'asc' | 'desc';
  si?: number;
  hidden: boolean;
  pin?: 'left' | 'right';
}

export interface Declared {
  ids: string[];
  cols: Record<string, DeclaredCol>;
}

export interface UserState {
  grouping: string[];
  sorting: GridSortItem[];
  hidden: Record<string, boolean>;
  pinned: Record<string, 'left' | 'right'>;
  /** null: follow the declared order (the user has not reordered). */
  order: string[] | null;
}

export function declaredOf<T>(columns: AppGridColumn<T>[], ids: string[]): Declared {
  const cols: Record<string, DeclaredCol> = {};
  columns.forEach((c, i) => {
    cols[ids[i]] = {
      gi: c.groupIndex ?? undefined,
      so: c.sortOrder ?? undefined,
      si: c.sortIndex ?? undefined,
      hidden: c.visible === false,
      pin: c.fixed ? (c.fixedPosition === 'right' ? 'right' : 'left') : undefined,
    };
  });
  return { ids, cols };
}

export function declaredKeyOf(d: Declared): string {
  return JSON.stringify(d.ids.map((id) => {
    const c = d.cols[id];
    return [id, c.gi, c.so, c.si, c.hidden, c.pin];
  }));
}

/** Sort entries declared by the columns, by sortIndex (else column position). */
function declaredSorts(d: Declared): GridSortItem[] {
  return d.ids
    .map((id, i) => ({ id, o: d.cols[id].so, si: d.cols[id].si ?? i }))
    .filter((x) => x.o)
    .sort((a, b) => a.si - b.si)
    .map((x) => ({ id: x.id, desc: x.o === 'desc' }));
}

export function initialUserState(d: Declared, saved?: Partial<SavedColumnState>): UserState {
  const known = (id: string) => !!d.cols[id];
  const grouping = saved?.grouping
    ? saved.grouping.filter(known)
    : d.ids.filter((id) => d.cols[id].gi != null).sort((a, b) => d.cols[a].gi! - d.cols[b].gi!);
  const sorting = saved?.sorting ? saved.sorting.filter((s) => known(s.id)) : declaredSorts(d);
  const hidden: Record<string, boolean> = {};
  if (saved?.hidden) {
    for (const id of saved.hidden) if (known(id)) hidden[id] = true;
  } else {
    for (const id of d.ids) if (d.cols[id].hidden) hidden[id] = true;
  }
  const pinned: Record<string, 'left' | 'right'> = {};
  if (saved?.pinned) {
    for (const [id, p] of Object.entries(saved.pinned)) if (known(id)) pinned[id] = p;
  } else {
    for (const id of d.ids) if (d.cols[id].pin) pinned[id] = d.cols[id].pin!;
  }
  let order: string[] | null = null;
  if (saved?.order) {
    const kept = saved.order.filter(known);
    order = mergeOrder(kept, d.ids);
  }
  return { grouping, sorting, hidden, pinned, order };
}

/**
 * Columns new to `ids` go in after their nearest declared predecessor that is
 * already in `order` (or first, when none is); columns no longer declared are
 * dropped. The user's relative order of the rest is kept.
 */
export function mergeOrder(order: string[], ids: string[]): string[] {
  const live = new Set(ids);
  const out = order.filter((id) => live.has(id));
  const placed = new Set(out);
  ids.forEach((id, i) => {
    if (placed.has(id)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const k = out.indexOf(ids[j]);
      if (k >= 0) {
        at = k + 1;
        break;
      }
    }
    out.splice(at, 0, id);
    placed.add(id);
  });
  return out;
}

/** The user's state after the declarations went from `prev` to `next`: only what changed is adopted. */
export function adoptDeclared(prev: Declared, next: Declared, cur: UserState): UserState {
  const exists = (id: string) => !!next.cols[id];
  const before = (id: string): DeclaredCol | undefined => prev.cols[id];

  // Grouping: columns whose groupIndex changed leave and re-enter at their new index.
  const giChanged = next.ids.filter((id) => before(id)?.gi !== next.cols[id].gi);
  let grouping = cur.grouping.filter((id) => exists(id) && !giChanged.includes(id));
  if (giChanged.length) {
    grouping = [...grouping];
    for (const id of giChanged.filter((x) => next.cols[x].gi != null).sort((a, b) => next.cols[a].gi! - next.cols[b].gi!)) {
      grouping.splice(Math.min(next.cols[id].gi!, grouping.length), 0, id);
    }
  }

  // Sorting: same, per column, on sortOrder / sortIndex.
  const soChanged = next.ids.filter((id) => before(id)?.so !== next.cols[id].so || before(id)?.si !== next.cols[id].si);
  let sorting = cur.sorting.filter((s) => exists(s.id) && !soChanged.includes(s.id));
  if (soChanged.length) {
    sorting = [...sorting];
    const adds = soChanged
      .filter((id) => next.cols[id].so)
      .map((id) => ({ id, si: next.cols[id].si }))
      .sort((a, b) => (a.si ?? Infinity) - (b.si ?? Infinity));
    for (const a of adds) {
      const at = a.si == null ? sorting.length : Math.min(a.si, sorting.length);
      sorting.splice(at, 0, { id: a.id, desc: next.cols[a.id].so === 'desc' });
    }
  }

  const hidden = { ...cur.hidden };
  const pinned = { ...cur.pinned };
  for (const id of Object.keys(hidden)) if (!exists(id)) delete hidden[id];
  for (const id of Object.keys(pinned)) if (!exists(id)) delete pinned[id];
  for (const id of next.ids) {
    const a = before(id);
    const b = next.cols[id];
    if (!a || a.hidden !== b.hidden) {
      if (b.hidden) hidden[id] = true;
      else delete hidden[id];
    }
    if (!a || a.pin !== b.pin) {
      if (b.pin) pinned[id] = b.pin;
      else delete pinned[id];
    }
  }

  const order = cur.order ? mergeOrder(cur.order, next.ids) : null;
  return { grouping, sorting, hidden, pinned, order };
}

/** Move `id` one visible place left (-1) or right (+1) among `visible`, inside `order`. */
export function moveInOrder(order: string[], visible: string[], id: string, dir: -1 | 1): string[] {
  const vi = visible.indexOf(id);
  const neighbour = visible[vi + dir];
  if (vi < 0 || neighbour == null) return order;
  const out = order.filter((x) => x !== id);
  const at = out.indexOf(neighbour);
  out.splice(dir < 0 ? at : at + 1, 0, id);
  return out;
}
